import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { BLOCK_KINDS, OTHER_KIND_FILTER_VALUE } from '@/blocks/domain/types';
import { BLOCK_STATUS_FILTER_VALUES } from '@/blocks/domain/types/BlockLifecycleStatus';

const KIND_FILTER_VALUES = ['all', ...BLOCK_KINDS, OTHER_KIND_FILTER_VALUE] as const;

/**
 * Single source of truth for the block library's URL filter state (`q`, `kind`, `status`).
 *
 * Returns the parsed URL values directly — nuqs's `useQueryStates` IS the state, with no
 * `useState` mirror and no sync effect layered on top. Mirroring the URL into local state via
 * an effect (`BlockLibraryFilters.tsx`'s pre-existing `[statusParam]` effect) is the root cause
 * of the "Show archived reverts itself" defect: the effect fires on every render the URL value
 * changes, and re-derives page state from it — which is fine until something resets `status`
 * out from under the URL and the effect stomps the URL value right back. Reading nuqs directly,
 * with no intermediate state to fall out of sync, removes that failure mode entirely.
 *
 * `status` is typed via `parseAsStringLiteral(BLOCK_STATUS_FILTER_VALUES)`, so `'all'` is a
 * first-class accepted value (not just a parser special case) and round-trips through the URL,
 * a re-render, and a full unmount + remount identically.
 */
export function useBlockLibraryUrlFilters() {
  return useQueryStates({
    q: parseAsString.withDefault(''),
    kind: parseAsStringLiteral(KIND_FILTER_VALUES).withDefault('all'),
    status: parseAsStringLiteral(BLOCK_STATUS_FILTER_VALUES).withDefault('all-active')
  });
}

export type BlockLibraryUrlFilters = ReturnType<typeof useBlockLibraryUrlFilters>[0];
